/**
 * Keeping the inbox current without re-reading it per message.
 *
 * A realtime message used to trigger a full inbox read. That read is not cheap
 * on either side: `GET /chat/sessions` opens with a `sweepInactive()` UPDATE and
 * then runs three queries, and `GET /groups/inbox` opens with
 * `expireStaleGroups()` and then loads *every message of every group the user
 * belongs to* to derive one preview and one unread count. Doing that once per
 * received message makes the cost of a conversation quadratic in its length.
 *
 * The socket payload already carries what the row displays, and carries it
 * identically — the chat preview is `content.slice(0, 140)` on both the event
 * and the stored row; the group preview is the full `content` on both. So the
 * patch here is exact, not a guess, and that is the whole reason it is safe.
 *
 * What is deliberately NOT derived: `inactiveAt`, `inactiveWarning`, `state`,
 * and the reveal fields. Receiving a message can move the inactivity clock, but
 * only when the receiver had already sent since the last mutual mark, which the
 * client has no way to know. Those converge through a single coalesced
 * reconciling read instead — so the read cost is bounded by *time*, not by
 * message count, and nothing goes quietly stale.
 *
 * No imports on purpose: this is reachable from the root `node --test` suites,
 * which cannot load anything that pulls `react-native`.
 */

export type PatchResult<T> = {
  rows: T[];
  /** False when the payload could not answer and the caller must read instead. */
  patched: boolean;
};

// ---- Chats -----------------------------------------------------------------

export type ChatRow = {
  id: string;
  lastMessage?: string;
  updatedAt: string;
};

export type ChatMessagePatch = {
  chatId: string;
  /** `content.slice(0, 140)`, exactly as the row stores it. */
  preview?: string;
  /** The server's timestamp. Ordering must not depend on the device clock. */
  sentAt?: string;
};

/**
 * Apply a received chat message to the session list.
 *
 * The chat that just received a message holds the maximum `last_message_at`, so
 * moving it to the front *is* the server's ordering rather than an approximation
 * of it — every other pair keeps the relative order the server last returned.
 */
export function applyChatMessage<T extends ChatRow>(
  rows: T[],
  patch: ChatMessagePatch,
): PatchResult<T> {
  if (patch.preview === undefined || patch.sentAt === undefined) {
    return { rows, patched: false };
  }
  const target = rows.find((row) => row.id === patch.chatId);
  if (!target) return { rows, patched: false };

  const patched: T = {
    ...target,
    lastMessage: patch.preview,
    updatedAt: patch.sentAt,
  };
  return {
    rows: [patched, ...rows.filter((row) => row.id !== patch.chatId)],
    patched: true,
  };
}

// ---- Groups ----------------------------------------------------------------

export type GroupRow = {
  id: string;
  unreadCount: number;
  lastMessage: string | null;
  lastMessageAt: string | null;
  /** The row's sort key — patched together with `lastMessageAt`, because the
   * inbox orders by it and a key left stale would hold the row in place while
   * its preview says otherwise. */
  updatedAt: string;
};

export type GroupMessagePatch = {
  groupId: string;
  preview?: string;
  sentAt?: string;
  /** The group open on screen. It is being read, so it never goes unread. */
  activeGroupId?: string | null;
};

/**
 * Apply a received group message to the group inbox.
 *
 * The +1 is exact rather than optimistic: `notifyGroupMessage` filters the
 * sender out before emitting, so every event the client sees is a message the
 * server's own `unreadCount` would also have counted.
 */
export function applyGroupMessage<T extends GroupRow>(
  rows: T[],
  patch: GroupMessagePatch,
): PatchResult<T> {
  if (patch.preview === undefined || patch.sentAt === undefined) {
    return { rows, patched: false };
  }
  const target = rows.find((row) => row.id === patch.groupId);
  if (!target) return { rows, patched: false };

  const reading = patch.activeGroupId === patch.groupId;
  const patched: T = {
    ...target,
    unreadCount: reading ? 0 : target.unreadCount + 1,
    lastMessage: patch.preview,
    lastMessageAt: patch.sentAt,
    updatedAt: patch.sentAt,
  };
  return {
    rows: [patched, ...rows.filter((row) => row.id !== patch.groupId)],
    patched: true,
  };
}

// ---- One inbox order -------------------------------------------------------

export type OrderedInboxRow = {
  id: string;
  kind: string;
  /** ISO timestamp: latest viewer-visible activity, from the row's server DTO. */
  updatedAt: string;
};

/**
 * The one rule that orders the inbox: latest activity first, whatever kind of
 * row carries it.
 *
 * This replaces concatenating groups above one-to-one chats. That layout was a
 * deliberate early choice — "groups are time-limited so they outrank" — and it
 * meant a chat that received a message a second ago sat below a group quiet
 * for a day; a reader hunting the unread thing had to scroll past every group
 * to find it. Both row kinds now ship the same `updatedAt` semantics (latest
 * visible message, else when the row appeared in this viewer's inbox), so the
 * comparison is meaningful across kinds.
 *
 * Total on purpose, the same rule the API's cursors follow: timestamps tie —
 * every rowless pair shares a fallback second — and a comparator that returns
 * 0 leaves the winner to the sort's whim, which reads as rows swapping places
 * between renders. Ties fall to kind, then id.
 *
 * An unparseable timestamp sorts LAST, not first: a row whose key is broken
 * should sink quietly, not squat on top of the inbox.
 *
 * Both sides' timestamps are only comparable because the API runs in UTC. Group
 * keys descend from a `timestamp WITHOUT time zone` column and chat keys from a
 * `timestamptz` one, so a non-UTC API process shifts one side and not the
 * other — measured at an hour. The server refuses to boot outside UTC for this
 * reason (`backend/api/src/config/utc-runtime.ts`); nothing here can detect it,
 * which is why it is enforced there.
 */
export function inboxOrder(a: OrderedInboxRow, b: OrderedInboxRow): number {
  const aTime = Date.parse(a.updatedAt) || 0;
  const bTime = Date.parse(b.updatedAt) || 0;
  if (bTime !== aTime) return bTime - aTime;
  if (a.kind !== b.kind) return a.kind < b.kind ? -1 : 1;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

// ---- Paging two merged sources ---------------------------------------------

export type PagedSource = {
  /** `updatedAt` of the OLDEST row this source has loaded; null when empty. */
  frontier: string | null;
  /** Whether the source has another page behind its frontier. */
  more: boolean;
};

/**
 * Which source to advance when the reader reaches the end of a merged list.
 *
 * The Inactive tab renders one timeline from two independently-paged sources
 * (dormant one-to-one chats, ended groups). Rows below the newer frontier are
 * not final: the OTHER source's unloaded rows may still belong above them —
 * which is why the source whose frontier is NEWER is the one that pages, not
 * the one the reader happens to have scrolled past. Advancing both on every
 * pull would be twice the reads for the same scroll; advancing the older
 * frontier first would interleave late rows above where the reader already
 * was.
 *
 * A source with nothing loaded but more behind advances first — its whole
 * section is above everything it has shown, which is nothing.
 */
export function nextInactiveLoad(
  chat: PagedSource,
  group: PagedSource,
): "chat" | "group" | "none" {
  if (!chat.more && !group.more) return "none";
  if (!chat.more) return "group";
  if (!group.more) return "chat";
  if (chat.frontier === null) return "chat";
  if (group.frontier === null) return "group";
  const chatAt = Date.parse(chat.frontier) || 0;
  const groupAt = Date.parse(group.frontier) || 0;
  // Ties are arbitrary but must be deterministic; chat is the larger source.
  return chatAt >= groupAt ? "chat" : "group";
}

// ---- The reconciling read --------------------------------------------------

type Handle = unknown;

type CoalesceOptions = {
  /**
   * The longest a derived field may lag. Trailing rather than leading: a burst
   * costs one read at the end of it, not one at the start and one at the end.
   */
  intervalMs?: number;
  /**
   * A floor between reads, which `requestNow` may not jump.
   *
   * `intervalMs` bounds a BURST; this bounds a STREAM. They are different
   * problems and the inbox only ever had the first. A burst ends, so the
   * in-flight guard plus one trailing read is the whole answer. A stream does
   * not: every event calls `requestNow`, which cancels whatever was scheduled
   * and reads immediately, so arrivals landing faster than a read completes
   * produce back-to-back reads for as long as they keep coming. That is what
   * a busy venue does to Home (`docs/architecture/arrival-fan-out.md`).
   *
   * Leading edge preserved on purpose: the first event after a quiet spell
   * still reads AT ONCE. Only the second and later within the floor are
   * degraded to a scheduled read, so a quiet space stays instant and a crowded
   * one stays bounded. `0` — the default — keeps the original behaviour
   * exactly, which is what the chat and group inboxes still want.
   */
  minIntervalMs?: number;
  /** Injectable for tests. */
  schedule?: (fn: () => void, ms: number) => Handle;
  cancelScheduled?: (handle: Handle) => void;
  now?: () => number;
};

export type CoalescedRefresh = {
  /**
   * Something changed that the patch already displayed correctly. Converge the
   * fields it could not derive, within the window. Repeat calls are free.
   */
  request: () => void;
  /**
   * Something changed that the patch could **not** display — a conversation the
   * client has never seen, a reveal. Waiting out the window would leave the user
   * looking at a stale inbox, so read now.
   */
  requestNow: () => void;
  /** Drop a scheduled read — on sign-out, where the session is already gone. */
  cancel: () => void;
  isPending: () => boolean;
};

const DEFAULT_INTERVAL_MS = 15_000;

export function createCoalescedRefresh(
  run: () => Promise<unknown>,
  options: CoalesceOptions = {},
): CoalescedRefresh {
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const minIntervalMs = options.minIntervalMs ?? 0;
  const now = options.now ?? (() => Date.now());
  const schedule =
    options.schedule ??
    ((fn: () => void, ms: number) => setTimeout(fn, ms) as unknown as Handle);
  const cancelScheduled =
    options.cancelScheduled ??
    ((handle: Handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));

  let handle: Handle | null = null;
  let running = false;
  /** An event arrived while a read was in flight, so that read may have missed it. */
  let missed = false;
  /**
   * When the last read STARTED, not when it finished — the floor is a rate,
   * and measuring from the end would let a slow read buy a shorter gap after
   * it, which is backwards: a slow read is evidence of load.
   */
  let lastRunAt: number | null = null;

  function fire(): void {
    handle = null;
    running = true;
    lastRunAt = now();
    // Swallowed deliberately: this runs in response to a socket event, not to
    // anything the user did, and each store already renders its own failure
    // state. An unowned rejection would surface as an error over whatever they
    // are looking at.
    void run()
      .catch(() => {})
      .finally(() => {
        running = false;
        if (missed) {
          missed = false;
          api.request();
        }
      });
  }

  const api: CoalescedRefresh = {
    isPending: () => handle !== null,
    request: () => {
      if (running) {
        // The read started before this event, so it cannot be trusted to
        // include it. Follow it with one more rather than dropping it.
        missed = true;
        return;
      }
      if (handle !== null) return;
      handle = schedule(fire, intervalMs);
    },
    requestNow: () => {
      if (running) {
        missed = true;
        return;
      }
      // The floor, and the only thing that separates this from `request`.
      // Not yet elapsed means this caller does not get to read now — but the
      // event is NOT dropped: it becomes a scheduled read for the remainder,
      // so the list still converges, just on the rate the caller asked for.
      const waitMs =
        minIntervalMs > 0 && lastRunAt !== null
          ? minIntervalMs - (now() - lastRunAt)
          : 0;
      if (waitMs > 0) {
        // An already-scheduled read is left alone rather than pulled earlier:
        // it fires at or after the floor either way, and rescheduling on every
        // event is how a trailing read gets pushed forever into the future.
        if (handle === null) handle = schedule(fire, waitMs);
        return;
      }
      if (handle !== null) {
        cancelScheduled(handle);
        handle = null;
      }
      fire();
    },
    cancel: () => {
      missed = false;
      if (handle === null) return;
      cancelScheduled(handle);
      handle = null;
    },
  };

  return api;
}
